import React from 'react';
import PropTypes from 'prop-types';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import { buildUseStyles, useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useWizardComplete from 'Views/Page/Hooks/useWizardComplete';
import WizardStandardHeader from 'Components/ComponentLibrary/Components/Uncategorized/WizardStandardHeader';
import {
  FDBox,
  FDStack,
  FDPaper,
} from '@facet-wealth/design';
import StepNavigator from 'Components/ComponentLibrary/Molecules/StepNavigator';
import { wizardClasses } from 'Views/Page/Content/Templates/WizardTemplates/wizardCommonStyles';
import CircularProgress from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/CircularProgress';
import Title from 'Components/ComponentLibrary/Atoms/Text/Title';
import Subtitle from 'Components/ComponentLibrary/Atoms/Text/Subtitle';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import RichTextDisplay from 'Components/UI/RichTextDisplay';
import { useSnackbarContext } from 'Context/SnackbarContext';

const useStyles = buildUseStyles('SelectInstitutionTemplate', (theme) => ({
  root: {
    ...wizardClasses.root(theme),
    margin: 'unset',
  },
  footer: {
    position: 'fixed',
    zIndex: 1,
    bottom: 0,
    width: '100%',
  },
}));

const ScenarioReviewScheduledTransfersToDeleteStepTemplate = ({
  resource,
}) => {
  const [loading, setLoading] = React.useState(false);
  const { setPageContentCollection } = usePageContentContext();
  const { displayErrorMessage } = useSnackbarContext();
  const theme = useTheme();
  const classes = useStyles(theme);

  const { handleNext } = useWizardComplete();

  // Enable the big loader while we make the next request. This request takes a while.
  const onNext = async () => {
    setLoading(true);

    try {
      const coll = await resource.submit();
      setPageContentCollection(coll);
    } catch (err) {
      displayErrorMessage(err, err.errors);
    } finally {
      setLoading(false);
      handleNext();
    }
  };

  const subtitle = (Array.isArray(resource.attr('subtitle'))) ? resource.attr('subtitle').join(' ') : resource.attr('subtitle');

  const titleContent = (
    <FDStack direction={'column'} alignItems={'center'} spacing={3}>
      <Title
        text={resource.attr('title')}
        test="scenarioReview"
      />
      <Subtitle
        text={subtitle}
        test="wizard-step-subtitle"
      />
      <FDBox padding={400}>
        {resource.attr('summary') ? (
          <FDPaper appearance={'flat'} color={'subtle'} padding={'400'}>
            <RichTextDisplay
              align="left"
              color="mediumGray"
              content={resource.attr('summary')}
              variant="h6"
            />
          </FDPaper>
        ) : null}
      </FDBox>
    </FDStack>
  );

  const loadingContent = (
    <FDStack direction={'column'} alignItems={'center'} spacing={3}>
      <Title text={resource.attr('loadingTitle')} />
      <Subtitle text={resource.attr('loadingSubtitle')} />
      <br />
      <CircularProgress size={50} />
    </FDStack>
  );

  return (
    <Box className={classes.root}>
      <WizardStandardHeader
        color={'primary'}
        title={resource.attr('moduleName')}
      />

      <FDBox padding={3}>
        <FDStack direction={'column'} alignItems={'center'} spacing={3}>
          {loading ? loadingContent : titleContent}
        </FDStack>
      </FDBox>

      <div className={classes.footer}>
        <StepNavigator
          onNext={onNext}
          disabled={loading}
          isLoading={loading}
        >
          {'Select Scenario'}
        </StepNavigator>
      </div>
    </Box>
  );
};

ScenarioReviewScheduledTransfersToDeleteStepTemplate.propTypes = {
  resource: PropTypes.object.isRequired,
};

export default ScenarioReviewScheduledTransfersToDeleteStepTemplate;
